import React, { useState, useEffect } from 'react';
import { formatCurrency as fmt } from '../../lib/format';
import { CreditCard, Building, Info, AlertCircle, Banknote, MapPin, Home } from 'lucide-react';
import { apiClient } from '../../lib/api';

// Updated payment method types to match backend
export type NewPaymentMethod = 'patient_pay' | 'physician_billed';
export type ShippingDestination = 'patient' | 'clinic';

interface NewPaymentMethodSelectorProps {
  selectedMethod: NewPaymentMethod;
  onMethodChange: (method: NewPaymentMethod) => void;
  shippingDestination?: ShippingDestination;
  onShippingDestinationChange?: (dest: ShippingDestination) => void;
  patientName?: string;
  clinicName?: string;
  clinicAddress?: {
    street?: string;
    city?: string;
    state?: string;
    zipCode?: string;
  };
  prescriptions?: any[];
  patientPayEnabled: boolean; // Whether physician has patient pay enabled
  totalAmount: number;
  patientAddress?: {
    street: string;
    street2?: string;
    city: string;
    state: string;
    zipCode: string;
  };
  loadingPatient?: boolean;
}

export const NewPaymentMethodSelector: React.FC<NewPaymentMethodSelectorProps> = ({
  selectedMethod,
  onMethodChange,
  shippingDestination = 'patient',
  onShippingDestinationChange,
  patientName,
  clinicName = 'Your Clinic',
  clinicAddress,
  prescriptions: _prescriptions,
  patientPayEnabled,
  totalAmount,
  patientAddress,
  loadingPatient
}) => {
  const [showInfo, setShowInfo] = useState(false);

  // Pull the physician's saved cards so we can show which one will be
  // charged automatically when they select physician_billed. Only fetch
  // once — the list is stable across the order flow.
  const [defaultCard, setDefaultCard] = useState<{ brand?: string; last4?: string } | null>(null);
  const [cardsLoaded, setCardsLoaded] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.getPaymentMethods();
        if (cancelled) return;
        if (res.success && res.data) {
          const defaultId = res.data.defaultPaymentMethodId;
          const pick = (res.data.paymentMethods || []).find(
            (pm: any) => pm.payment_method_id === defaultId
          ) || (res.data.paymentMethods || [])[0];
          if (pick?.card) {
            setDefaultCard({ brand: pick.card.card_brand, last4: pick.card.last4 });
          }
        }
      } catch {
        // silent — UI just won't show the card banner
      } finally {
        if (!cancelled) setCardsLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Fetch this physician's shipping rate so we can break out the total.
  // Rate is keyed on physician/AM and doesn't depend on cart contents, so
  // fetch once.
  const [shippingRate, setShippingRate] = useState(0);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.getShippingRate(false);
        if (cancelled) return;
        if (res.success && res.data && typeof res.data.shippingRate === 'number') {
          setShippingRate(res.data.shippingRate);
        }
      } catch {
        // silent — fall back to subtotal-only display
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const displayPatientName = patientName || 'the patient';
  const grandTotal = totalAmount + shippingRate;
  const formattedTotal = fmt(grandTotal);
  const formattedSubtotal = fmt(totalAmount);
  const formattedShipping = fmt(shippingRate);

  const paymentOptions = [
    {
      id: 'patient_pay' as NewPaymentMethod,
      title: 'Patient Pays Online',
      icon: CreditCard,
      description: 'Patient pays securely online before pharmacy fulfillment',
      details: [
        'Secure online payment processing',
        'Credit/debit card and digital wallet support',
        'Payment required before prescription fulfillment',
        'Automated payment confirmation and receipts',
        'Real-time payment status tracking'
      ],
      pricing: `Patient pays ${formattedTotal} online`,
      recommended: true,
      enabled: patientPayEnabled,
      disabledReason: patientPayEnabled ? null : 'Patient payments not enabled for your account'
    },
    {
      id: 'physician_billed' as NewPaymentMethod,
      title: 'Physician/Clinic Billed',
      icon: Building,
      description: 'Traditional billing - clinic pays and handles patient billing separately',
      details: [
        'Clinic/physician pays for medications',
        'Standard account billing process',
        'Clinic handles patient billing separately',
        'Immediate prescription fulfillment',
        'Monthly consolidated billing'
      ],
      pricing: `${clinicName} billed ${formattedTotal}`,
      recommended: false,
      enabled: true,
      disabledReason: null
    }
  ];

  // Auto-select the only available option if patient pay is disabled
  React.useEffect(() => {
    if (!patientPayEnabled && selectedMethod === 'patient_pay') {
      onMethodChange('physician_billed');
    }
  }, [patientPayEnabled, selectedMethod, onMethodChange]);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Payment Method</h3>
          <p className="text-sm text-gray-600">
            Choose how medications will be paid for <span className="font-medium">{displayPatientName}</span>
          </p>
          {totalAmount > 0 && (
            <div className="text-sm text-gray-700 mt-2 space-y-0.5">
              {shippingRate > 0 ? (
                <>
                  <div className="flex justify-between max-w-xs">
                    <span>Subtotal</span>
                    <span>{formattedSubtotal}</span>
                  </div>
                  <div className="flex justify-between max-w-xs">
                    <span>Shipping</span>
                    <span>{formattedShipping}</span>
                  </div>
                  <div className="flex justify-between max-w-xs font-semibold text-gray-900 pt-0.5 border-t border-gray-200">
                    <span>Total</span>
                    <span>{formattedTotal}</span>
                  </div>
                </>
              ) : (
                <p className="text-sm font-medium text-gray-900">
                  Total: {formattedTotal}
                </p>
              )}
            </div>
          )}
          {patientAddress && (
            <div className="mt-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm font-medium text-blue-900 mb-1">Shipping Address:</p>
              <p className="text-sm text-blue-800">
                {patientAddress.street}<br/>
                {patientAddress.street2 && <>{patientAddress.street2}<br/></>}
                {patientAddress.city}, {patientAddress.state} {patientAddress.zipCode}
              </p>
              <p className="text-xs text-blue-600 mt-1">
                💡 Patient can confirm/update address when paying online
              </p>
            </div>
          )}
          {loadingPatient && (
            <p className="text-sm text-gray-500 mt-1">Loading patient details...</p>
          )}
        </div>
        <button
          onClick={() => setShowInfo(!showInfo)}
          className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
        >
          <Info className="h-5 w-5" />
        </button>
      </div>

      {showInfo && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start space-x-3">
            <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-3">Payment Processing Models:</p>
              <div className="space-y-3">
                <div>
                  <p className="font-medium text-blue-900">Patient Pays Online:</p>
                  <p>Patient → Pays Online → Pharmacy Fulfills → Platform Fee Collected</p>
                  <p className="text-xs text-blue-700 mt-1">
                    Patients pay securely through our platform before prescription fulfillment
                  </p>
                </div>
                <div>
                  <p className="font-medium text-blue-900">Physician Billed:</p>
                  <p>Clinic → Billed Monthly → Handles Patient Billing → Immediate Fulfillment</p>
                  <p className="text-xs text-blue-700 mt-1">
                    Traditional billing model where clinic pays and bills patient separately
                  </p>
                </div>
                <div className="bg-blue-100 p-2 rounded mt-2">
                  <p className="text-xs font-medium text-blue-900">
                    Note: Patient payment processing provides better cash flow and reduces billing overhead
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Options */}
      <div className="space-y-4">
        {paymentOptions.map((option) => {
          const Icon = option.icon;
          const isSelected = selectedMethod === option.id;
          const isDisabled = !option.enabled;
          
          return (
            <div
              key={option.id}
              className={`relative border rounded-lg p-6 transition-all ${
                isDisabled
                  ? 'border-gray-200 bg-gray-50 opacity-60'
                  : isSelected
                  ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200 cursor-pointer'
                  : 'border-gray-200 hover:border-gray-300 hover:shadow-sm cursor-pointer'
              }`}
              onClick={() => option.enabled && onMethodChange(option.id)}
            >
              {option.recommended && option.enabled && (
                <div className="absolute -top-2 left-4">
                  <span className="bg-green-600 text-white text-xs px-2 py-1 rounded-full">
                    Recommended
                  </span>
                </div>
              )}

              {isDisabled && (
                <div className="absolute -top-2 right-4">
                  <span className="bg-gray-500 text-white text-xs px-2 py-1 rounded-full">
                    Unavailable
                  </span>
                </div>
              )}

              <div className="flex items-start space-x-4">
                <div className={`p-2 rounded-lg ${
                  isDisabled
                    ? 'bg-gray-200 text-gray-400'
                    : isSelected 
                    ? 'bg-blue-100 text-blue-600' 
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  <Icon className="h-6 w-6" />
                </div>

                <div className="flex-1">
                  <h3 className={`text-lg font-medium ${
                    isDisabled
                      ? 'text-gray-400'
                      : isSelected 
                      ? 'text-blue-900' 
                      : 'text-gray-900'
                  }`}>
                    {option.title}
                  </h3>
                  <p className={`mt-1 ${
                    isDisabled ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    {option.description}
                  </p>

                  {isDisabled && option.disabledReason && (
                    <div className="mt-2 p-2 bg-gray-100 rounded text-sm text-gray-600 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-2 text-gray-500" />
                      {option.disabledReason}
                    </div>
                  )}

                  <div className="mt-3">
                    <p className={`text-sm font-medium flex items-center ${
                      isDisabled
                        ? 'text-gray-400'
                        : isSelected 
                        ? 'text-blue-700' 
                        : 'text-gray-700'
                    }`}>
                      <Banknote className="h-4 w-4 mr-1" />
                      {option.pricing}
                    </p>
                  </div>

                  {/* Details list */}
                  <ul className="mt-3 space-y-1">
                    {option.details.map((detail, index) => (
                      <li key={index} className={`text-sm flex items-center ${
                        isDisabled ? 'text-gray-400' : 'text-gray-600'
                      }`}>
                        <div className={`w-1.5 h-1.5 rounded-full mr-2 ${
                          isDisabled ? 'bg-gray-300' : 'bg-gray-400'
                        }`}></div>
                        {detail}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Selection indicator */}
                <div className="flex-shrink-0">
                  {isSelected && !isDisabled && (
                    <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Card on file — only for physician_billed, only if a saved card exists */}
      {selectedMethod === 'physician_billed' && cardsLoaded && (
        defaultCard ? (
          <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
            <CreditCard className="h-5 w-5 text-green-700 mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <p className="font-medium text-green-900">
                Card on file will be charged automatically
              </p>
              <p className="text-green-800 mt-0.5">
                {defaultCard.brand ? `${defaultCard.brand.charAt(0).toUpperCase() + defaultCard.brand.slice(1)} ` : 'Card '}
                ending in {defaultCard.last4 || '••••'} — {formattedTotal}
              </p>
            </div>
          </div>
        ) : (
          <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-amber-700 mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <p className="font-medium text-amber-900">No card on file</p>
              <p className="text-amber-800 mt-0.5">
                You'll receive an invoice to pay manually. Add a card in Settings to enable automatic charges.
              </p>
            </div>
          </div>
        )
      )}

      {/* Shipping Destination - only for physician_billed */}
      {selectedMethod === 'physician_billed' && onShippingDestinationChange && (
        <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <h4 className="text-sm font-medium text-gray-900 mb-3">Ship To</h4>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => onShippingDestinationChange('patient')}
              className={`flex items-center gap-2 p-3 rounded-lg border text-left transition-all ${
                shippingDestination === 'patient'
                  ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-200'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <Home className={`h-5 w-5 ${shippingDestination === 'patient' ? 'text-blue-600' : 'text-gray-400'}`} />
              <div>
                <p className={`text-sm font-medium ${shippingDestination === 'patient' ? 'text-blue-900' : 'text-gray-700'}`}>
                  Patient Address
                </p>
                {patientAddress && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    {patientAddress.city}, {patientAddress.state}
                  </p>
                )}
              </div>
            </button>
            <button
              onClick={() => onShippingDestinationChange('clinic')}
              className={`flex items-center gap-2 p-3 rounded-lg border text-left transition-all ${
                shippingDestination === 'clinic'
                  ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-200'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <MapPin className={`h-5 w-5 ${shippingDestination === 'clinic' ? 'text-blue-600' : 'text-gray-400'}`} />
              <div>
                <p className={`text-sm font-medium ${shippingDestination === 'clinic' ? 'text-blue-900' : 'text-gray-700'}`}>
                  Clinic
                </p>
                {clinicAddress?.city && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    {clinicAddress.city}, {clinicAddress.state}
                  </p>
                )}
              </div>
            </button>
          </div>
          {shippingDestination === 'clinic' && (!clinicAddress?.street || !clinicAddress?.city) && (
            <div className="mt-2 flex items-center gap-1.5 text-amber-700 text-xs">
              <AlertCircle className="h-3.5 w-3.5" />
              <span>Please ensure your clinic address is set in Settings.</span>
            </div>
          )}
        </div>
      )}

      {/* Info Section */}
      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
        <button
          onClick={() => setShowInfo(!showInfo)}
          className="flex items-center justify-between w-full text-left"
        >
          <span className="font-medium text-gray-900">Understanding Payment Methods</span>
          <Info className={`h-5 w-5 text-gray-500 transition-transform ${showInfo ? 'rotate-180' : ''}`} />
        </button>
        
        {showInfo && (
          <div className="mt-3 space-y-3 text-sm text-gray-600">
            <div>
              <strong>Patient Pays Online:</strong> <strong>{displayPatientName}</strong> will pay securely 
              through our platform using credit/debit cards or digital wallets before prescription fulfillment.
            </div>
            <div>
              <strong>Physician Billed:</strong> <strong>{clinicName}</strong> will be billed monthly and 
              can handle patient billing separately. Prescriptions are fulfilled immediately.
            </div>
            {!patientPayEnabled && (
              <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mt-3">
                <div className="flex items-center">
                  <AlertCircle className="h-4 w-4 text-yellow-600 mr-2" />
                  <span className="text-yellow-800 font-medium">
                    Patient payment processing is not enabled for your account. 
                    Contact your account manager to enable this feature.
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
